import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useMostRecentInput, RICH_TEXT_EDITOR } from 'hooks/useMostRecentInput';
import {
  getExpandedPicklistSelection,
  getWordRangeAtPoint,
  getParagraphRangeAtPoint,
  toSafeSlatePoint,
} from 'domains/reporter/RichTextEditor/utils';
import {
  Range,
  Transforms,
  Point,
  ReactEditor,
  useSlateStatic,
} from 'domains/reporter/RichTextEditor/core';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { MouseDragStyle } from 'generated/graphql';
import { WithApolloContext } from 'modules/Apollo/types';
import { useUnifiedEditorElement } from '../../ui/UnifiedEditorElementContext';

export const SINGLE_CLICK_EVENT_ID = 1;
export const DOUBLE_CLICK_EVENT_ID = 2;
export const TRIPLE_CLICK_EVENT_ID = 3;
// distance in pixels to activate word select when dragging in inverted mode to not make it noisy on small movements
const INVERTED_DRAG_ACTIVATION_DELTA_PX = 3;

const getRangeStart = (range: Range) =>
  Point.isBefore(range.anchor, range.focus) ? range.anchor : range.focus;
const getRangeEnd = (range: Range) =>
  Point.isBefore(range.anchor, range.focus) ? range.focus : range.anchor;

type SelectionState = {
  activeWordRange: Range | null;
  queuedWordRange: Range | null;
  mouseDownPosition: { x: number; y: number } | null;
  baseWordAnchor: Point | null;
  collapsedSelectionTimeout: ReturnType<typeof setTimeout> | null;
  lastSyncedSelection: Range | null;
  pendingCaretPoint: Point | null;
  isDragging: boolean;
  isWordSelectMode: boolean;
};

const clearPendingWordSelection = (selectionState: SelectionState) => {
  selectionState.queuedWordRange = null;
  selectionState.mouseDownPosition = null;
};

/**
 * Hook to enable word-wise selection expansion on doubleclick + drag,
 * and paragraph selection on triple click, mimicking native browser behavior.
 */
export const useExpandSelection = ({
  forceExpandPicklist = false,
  context,
}: {
  forceExpandPicklist?: boolean;
} & WithApolloContext) => {
  const { data } = useCurrentUser({ context });
  const { isMousePressed } = useMostRecentInput({ id: RICH_TEXT_EDITOR, context });
  const editor = useSlateStatic();
  const unifiedEditorElement = useUnifiedEditorElement();

  const selectionStateRef = useRef<SelectionState>({
    activeWordRange: null,
    queuedWordRange: null,
    mouseDownPosition: null,
    baseWordAnchor: null,
    collapsedSelectionTimeout: null,
    lastSyncedSelection: null,
    pendingCaretPoint: null,
    isDragging: false,
    isWordSelectMode: false,
  });

  const isMousePressedRef = useRef(isMousePressed);

  // cancel any scheduled fallback collapse before we promote to a full word selection.
  const clearCollapsedSelectionTimeout = useCallback(() => {
    if (selectionStateRef.current.collapsedSelectionTimeout) {
      clearTimeout(selectionStateRef.current.collapsedSelectionTimeout);
      selectionStateRef.current.collapsedSelectionTimeout = null;
    }
  }, []);

  // Activates word select mode by setting the initial range, clearing the pending word range,
  // setting the word select mode flag, dragging flag, clearing the initial mouse position,
  // clearing the initial word anchor, and clearing the collapsed selection timeout
  const enterWordSelectMode = useCallback(
    (range: Range) => {
      selectionStateRef.current.activeWordRange = range;
      selectionStateRef.current.queuedWordRange = null;
      selectionStateRef.current.isWordSelectMode = true;
      selectionStateRef.current.isDragging = true;
      selectionStateRef.current.mouseDownPosition = null;
      selectionStateRef.current.baseWordAnchor = getRangeStart(range);
      selectionStateRef.current.pendingCaretPoint = null;
      clearCollapsedSelectionTimeout();
    },
    [clearCollapsedSelectionTimeout]
  );

  const resetDragTracking = useCallback(() => {
    selectionStateRef.current.isDragging = false;
    selectionStateRef.current.isWordSelectMode = false;
    selectionStateRef.current.activeWordRange = null;
    selectionStateRef.current.queuedWordRange = null;
    selectionStateRef.current.mouseDownPosition = null;
    selectionStateRef.current.baseWordAnchor = null;
    selectionStateRef.current.pendingCaretPoint = null;
  }, []);

  const getSafeCollapsedPoint = useCallback(
    (domRange: globalThis.Range | null) => {
      const safePoint = domRange
        ? toSafeSlatePoint(editor, domRange.startContainer, domRange.startOffset)
        : null;

      if (safePoint) {
        return safePoint;
      }

      const currentSelection = editor.selection;
      if (!currentSelection) {
        return null;
      }

      return Point.isAfter(currentSelection.anchor, currentSelection.focus)
        ? currentSelection.anchor
        : currentSelection.focus;
    },
    [editor]
  );

  const scheduleCollapsedSelection = useCallback(
    (point: Point) => {
      const collapsedRange: Range = { anchor: point, focus: point };
      selectionStateRef.current.activeWordRange = collapsedRange;

      requestAnimationFrame(() => {
        Transforms.select(editor, collapsedRange);
      });
    },
    [editor]
  );

  const handleInvertedDoubleClick = useCallback(
    (domRange: globalThis.Range) => {
      const selectionState = selectionStateRef.current;

      clearPendingWordSelection(selectionState);
      selectionState.isWordSelectMode = false;
      selectionState.isDragging = true;
      selectionState.baseWordAnchor = null;

      const safePoint = getSafeCollapsedPoint(domRange);

      if (!safePoint) {
        selectionState.activeWordRange = null;
        return;
      }

      scheduleCollapsedSelection(safePoint);
    },
    [getSafeCollapsedPoint, scheduleCollapsedSelection]
  );

  const mouseDragStyleValue = data?.me?.reporterSettings?.textSelectionBehavior?.mouseDragStyle;
  const { mouseDragStyle, wordSelectActivationDetail, shouldSelectWordImmediately } =
    useMemo(() => {
      const normalizedStyle =
        mouseDragStyleValue === undefined || mouseDragStyleValue === null
          ? MouseDragStyle.Standard
          : mouseDragStyleValue;
      return {
        mouseDragStyle: normalizedStyle,
        wordSelectActivationDetail:
          normalizedStyle === MouseDragStyle.Inverted
            ? SINGLE_CLICK_EVENT_ID
            : DOUBLE_CLICK_EVENT_ID,
        shouldSelectWordImmediately: normalizedStyle !== MouseDragStyle.Inverted,
      };
    }, [mouseDragStyleValue]);

  const ensureEditorFocus = useCallback(() => {
    try {
      if (!ReactEditor.isFocused(editor)) {
        ReactEditor.focus(editor);
      }
    } catch {
      // editor may no longer be mounted; ignore
    }
  }, [editor]);

  // Ensures the editor keeps focus which solves the inverted mode bug where Slate collapses
  // selection mid drag or leaves the highlight stuck
  const applySelectionWithFallback = useCallback(
    (range: Range, options?: { skipFallback?: boolean; trackWordRange?: boolean }) => {
      ensureEditorFocus();

      // attempt regular selection
      Transforms.select(editor, range);

      // cache for next frame
      if (options?.trackWordRange) {
        selectionStateRef.current.lastSyncedSelection = range;
      }

      if (options?.skipFallback) {
        return;
      }

      // Keep the selection between frames by refocusing and reapplying
      requestAnimationFrame(() => {
        ensureEditorFocus();

        // update selection if its changed
        const currentSelection = editor.selection;
        const anchorMatches =
          currentSelection?.anchor && Point.equals(currentSelection.anchor, range.anchor);
        const focusMatches =
          currentSelection?.focus && Point.equals(currentSelection.focus, range.focus);
        if (!anchorMatches || !focusMatches) {
          Transforms.select(editor, range);
        }
      });
    },
    [editor, ensureEditorFocus]
  );

  // Reapply the last word range if the browser collapses selection during mid drag. Without this,
  // inverted single click drags would collapse after Slate and DOM selection sync
  useEffect(() => {
    const handleDomSelectionChange = () => {
      const domSelection = window.getSelection();
      if (!domSelection) {
        return;
      }

      const selectionState = selectionStateRef.current;

      if (selectionState.isWordSelectMode || selectionState.isDragging) {
        const lastRange = selectionState.lastSyncedSelection;
        if (!lastRange) {
          return;
        }

        const domCollapsed = domSelection.isCollapsed;
        // document.body is the default target for collapsed selection
        const anchorIsBody = domSelection.anchorNode === document.body;
        const focusIsBody = domSelection.focusNode === document.body;

        if (domCollapsed || anchorIsBody || focusIsBody) {
          // similar to the handleMouseUp function, when inverted drags previously hit this path the DOM would collapse so reapply the last word range on the next frame
          requestAnimationFrame(() => {
            applySelectionWithFallback(lastRange, { trackWordRange: true });
          });
        }
      } else if (!selectionState.pendingCaretPoint) {
        selectionState.lastSyncedSelection = null;
      }
    };

    document.addEventListener('selectionchange', handleDomSelectionChange, true);

    return () => {
      document.removeEventListener('selectionchange', handleDomSelectionChange, true);
    };
  }, [applySelectionWithFallback]);

  // Keep mouse press state up-to-date
  useEffect(() => {
    isMousePressedRef.current = isMousePressed;
  }, [isMousePressed]);

  // Isolate selection manipulation in useLayoutEffect to run in render cycle
  useLayoutEffect(() => {
    if (forceExpandPicklist && editor.selection) {
      const expandedSelection = getExpandedPicklistSelection(editor);
      if (expandedSelection) {
        Transforms.select(editor, expandedSelection);
      }
    }
  }, [forceExpandPicklist, editor.selection, editor]);

  // restore selection to click point and clear cached drag state
  const handleMouseUp = useCallback(() => {
    const selectionState = selectionStateRef.current;
    const hasDragged = selectionState.isDragging || selectionState.isWordSelectMode;

    // No drag occurred (e.g. inverted single click). Restore the selection to the original click point
    if (!hasDragged) {
      const collapsedPoint =
        selectionState.pendingCaretPoint ?? selectionState.queuedWordRange?.focus;
      if (collapsedPoint) {
        applySelectionWithFallback(
          { anchor: collapsedPoint, focus: collapsedPoint },
          { skipFallback: true }
        );
      }
    }

    // Always clear the cached word range when lifting the mouse so future drags start new
    selectionState.lastSyncedSelection = null;

    // Clear the collapsed selection timeout
    clearCollapsedSelectionTimeout();

    resetDragTracking();
  }, [applySelectionWithFallback, clearCollapsedSelectionTimeout, resetDragTracking]);

  const selectParagraphAtPoint = useCallback(
    (point: Point) => {
      const paragraphRange = getParagraphRangeAtPoint(editor, point);

      if (paragraphRange) {
        // Select the entire paragraph
        requestAnimationFrame(() => {
          Transforms.select(editor, paragraphRange);
        });
      }
    },
    [editor]
  );

  const selectWordImmediately = useCallback(
    (wordRange: Range, point: Point) => {
      requestAnimationFrame(() => {
        Transforms.select(editor, wordRange);
      });
      enterWordSelectMode(wordRange);
      selectionStateRef.current.pendingCaretPoint = point;
    },
    [editor, enterWordSelectMode]
  );

  const primeWordSelectForDrag = useCallback(
    (wordRange: Range, point: Point, event: MouseEvent) => {
      const selectionState = selectionStateRef.current;

      selectionState.queuedWordRange = wordRange;
      selectionState.activeWordRange = null;
      selectionState.isDragging = false;
      selectionState.isWordSelectMode = false;
      selectionState.mouseDownPosition = { x: event.clientX, y: event.clientY };
      selectionState.baseWordAnchor = getRangeStart(wordRange);
      selectionState.pendingCaretPoint = point;

      clearCollapsedSelectionTimeout();
    },
    [clearCollapsedSelectionTimeout]
  );

  const maybeActivateWordSelectMode = useCallback(
    (
      point: Point,
      cursorPosition: { x: number; y: number },
      getPointerWordRange: () => Range | null
    ) => {
      const selectionState = selectionStateRef.current;

      if (selectionState.isWordSelectMode) {
        return true;
      }

      const pendingWordRange = selectionState.queuedWordRange;

      if (pendingWordRange) {
        clearCollapsedSelectionTimeout();
        if (mouseDragStyle === MouseDragStyle.Inverted) {
          const initialMousePosition = selectionState.mouseDownPosition;
          if (!initialMousePosition) return false;

          const deltaX = cursorPosition.x - initialMousePosition.x;
          const deltaY = cursorPosition.y - initialMousePosition.y;
          const distance = Math.hypot(deltaX, deltaY);
          if (distance < INVERTED_DRAG_ACTIVATION_DELTA_PX) {
            return false;
          }

          const wordRangeAtPointer = getPointerWordRange();
          if (!wordRangeAtPointer) return false;

          enterWordSelectMode(pendingWordRange);

          const initialAnchor = selectionState.baseWordAnchor ?? getRangeStart(pendingWordRange);
          const pointerAnchor = getRangeStart(wordRangeAtPointer);
          const pointerFocus = getRangeEnd(wordRangeAtPointer);
          const initialFocus = getRangeEnd(pendingWordRange);
          const isPointerBeforeInitial = Point.isBefore(pointerAnchor, initialAnchor);
          const wordRange = isPointerBeforeInitial
            ? { anchor: pointerAnchor, focus: initialFocus }
            : { anchor: initialAnchor, focus: pointerFocus };

          applySelectionWithFallback(wordRange, { trackWordRange: true });
          return false;
        }

        enterWordSelectMode(pendingWordRange);
        return true;
      }

      if (selectionState.isDragging && selectionState.activeWordRange) {
        clearCollapsedSelectionTimeout();
        const charRange = { anchor: selectionState.activeWordRange.anchor, focus: point };
        applySelectionWithFallback(charRange);
        return false;
      }

      return false;
    },
    [
      applySelectionWithFallback,
      clearCollapsedSelectionTimeout,
      enterWordSelectMode,
      mouseDragStyle,
    ]
  );

  /**
   * - Double click (standard) immediately enters word-select mode.
   * - Single click (inverted) caches the word and waits for drag distance before promoting.
   * - Triple click selects the entire paragraph.
   */
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      const domRange = document.caretRangeFromPoint(e.clientX, e.clientY);

      if (!domRange) {
        handleMouseUp();
        return;
      }

      const selectionState = selectionStateRef.current;

      if (e.detail === TRIPLE_CLICK_EVENT_ID && isMousePressedRef.current) {
        e.preventDefault();
        e.stopPropagation();

        const point = toSafeSlatePoint(editor, domRange.startContainer, domRange.startOffset);
        if (point == null) {
          handleMouseUp();
          return;
        }
        selectParagraphAtPoint(point);
        return;
      } else if (
        e.detail === wordSelectActivationDetail &&
        (wordSelectActivationDetail === SINGLE_CLICK_EVENT_ID || isMousePressedRef.current)
      ) {
        const point = toSafeSlatePoint(editor, domRange.startContainer, domRange.startOffset);
        if (point == null) {
          handleMouseUp();
          return;
        }
        const wordRange = getWordRangeAtPoint(editor, point);

        if (!wordRange) {
          handleMouseUp();
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        if (shouldSelectWordImmediately) {
          selectWordImmediately(wordRange, point);
        } else {
          // if we're not selecting the word immediately, we need to cache the word and wait for the drag to cross the threshold
          primeWordSelectForDrag(wordRange, point, e);
        }
      } else if (mouseDragStyle === MouseDragStyle.Inverted && e.detail === DOUBLE_CLICK_EVENT_ID) {
        handleInvertedDoubleClick(domRange);
      }
    },
    [
      editor,
      handleMouseUp,
      selectParagraphAtPoint,
      wordSelectActivationDetail,
      shouldSelectWordImmediately,
      selectWordImmediately,
      primeWordSelectForDrag,
      mouseDragStyle,
      handleInvertedDoubleClick,
    ]
  );

  const buildWordRangeForDrag = useCallback(
    (point: Point, activeWordRange: Range, pointerWordRange: Range) => {
      if (mouseDragStyle === MouseDragStyle.Inverted) {
        const selectionState = selectionStateRef.current;
        const initialAnchor = selectionState.baseWordAnchor ?? getRangeStart(activeWordRange);
        const pointerAnchor = getRangeStart(pointerWordRange);
        const pointerFocus = getRangeEnd(pointerWordRange);
        const initialFocus = getRangeEnd(activeWordRange);
        const isPointerBeforeInitial = Point.isBefore(pointerAnchor, initialAnchor);
        return isPointerBeforeInitial
          ? { anchor: pointerAnchor, focus: initialFocus }
          : { anchor: initialAnchor, focus: pointerFocus };
      }

      const isBackward = Point.isBefore(point, activeWordRange.anchor);
      return isBackward
        ? { anchor: activeWordRange.focus, focus: pointerWordRange.anchor }
        : { anchor: activeWordRange.anchor, focus: pointerWordRange.focus };
    },
    [mouseDragStyle]
  );

  /**
   * Drag handler shared by both word select modes:
   * - Standard (double click + drag): anchor stays on the double clicked word while the focus
   *   grows toward the pointer word.
   * - Inverted (single click + drag after threshold): once the 3 px distance is met we reuse the
   *   same word select behavior. otherwise we keep character selection.
   */
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const isPrimaryButtonDown = (e.buttons & 1) === 1;
      const isDragging = isMousePressedRef.current || isPrimaryButtonDown;
      if (!isDragging) return;

      const domRange = document.caretRangeFromPoint(e.clientX, e.clientY);
      if (!domRange) return;

      const point = toSafeSlatePoint(editor, domRange.startContainer, domRange.startOffset);
      if (!point) return;

      let hasResolvedPointerWordRange = false;
      let cachedPointerWordRange: Range | null = null;
      const getPointerWordRange = () => {
        if (!hasResolvedPointerWordRange) {
          cachedPointerWordRange = getWordRangeAtPoint(editor, point);
          hasResolvedPointerWordRange = true;
        }
        return cachedPointerWordRange;
      };

      // If we're not already in word select mode, check if we should promote to word select based on the drag distance
      if (
        !maybeActivateWordSelectMode(point, { x: e.clientX, y: e.clientY }, getPointerWordRange)
      ) {
        return;
      }

      const activeWordRange = selectionStateRef.current.activeWordRange;
      if (!activeWordRange) return;

      const wordRangeAtPointer = getPointerWordRange();
      if (!wordRangeAtPointer) return;

      const wordRange = buildWordRangeForDrag(point, activeWordRange, wordRangeAtPointer);

      // Apply the expanded selection
      applySelectionWithFallback(wordRange, { trackWordRange: true });
    },
    [editor, applySelectionWithFallback, maybeActivateWordSelectMode, buildWordRangeForDrag]
  );

  // Register all global mouse handlers on mount
  useEffect(() => {
    const editorRef = unifiedEditorElement;

    editorRef?.addEventListener('mousedown', handleMouseDown);
    editorRef?.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      handleMouseUp();
      editorRef?.removeEventListener('mousedown', handleMouseDown);
      editorRef?.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, unifiedEditorElement]);

  return mouseDragStyle;
};
